import {
  Category,
  Client,
  ClientTechnicalNote,
  Company,
  DashboardState,
  Equipment,
  InstallationsMap,
  Notifications,
  Quote,
  QuoteNote,
  QuoteTasks,
  Receipt,
  RentContract,
  RentContractClient,
  RentContractPlan,
  RentReceipt,
  StatsData,
  TechniciansMap,
  UIValue,
  User,
  ContractInfo,
} from "./types";

export const apiFetch = async <TResponse, TBody = unknown>(
  method: string,
  path: string,
  query?: Record<string, string> | null,
  body?: TBody
): Promise<TResponse> => {
  const url = [process.env.REACT_APP_API, path];

  if (query) {
    url.push("?");
    url.push(new URLSearchParams(query).toString());
  }

  const options: RequestInit = {
    method,
    headers: {
      "content-type": "application/json",
      Authorization: localStorage.getItem("token") || "",
    },
    body: body ? JSON.stringify(body) : undefined,
  };

  const response = await fetch(url.join(""), options);

  if (!response.ok) {
    throw new Error("Failed API request");
  }

  return response.json();
};

export type ListResponse<T> = {
  data: T[];
  lastPage: number;
};

export type Ok = { success: boolean };

const version = (): Promise<string> =>
  apiFetch("GET", "/version", { t: Date.now().toString() });

const me = () => apiFetch<User>("GET", "/me");

type LoginResponse = {
  success: boolean;
  token?: string;
};
const login = (username: string, password: string) =>
  apiFetch<LoginResponse>("POST", "/login", null, { username, password });

const getCompanies = () => apiFetch<Company[]>("GET", "/get-companies");

const usersGetAll = () => apiFetch<User[]>("GET", "/users/get-all");

const usersGet = (uuid: string) => apiFetch<User>("GET", `/users/get/${uuid}`);

const usersUpdate = (uuid: string, user: User, password: string) =>
  apiFetch<Ok>("POST", `/users/update/${uuid}`, null, {
    user,
    password,
  });

const usersCreate = (user: User, password: string) =>
  apiFetch<Ok>("POST", "/users/create", null, { user, password });

export type ClientsSearchResponse = {
  uuid: string;
  client: Client;
  sellerName: string;
};
const clientsSearch = (companyUuid: string, searchTerm: string, page: string) =>
  apiFetch<ListResponse<ClientsSearchResponse>>("GET", "/clients/search", {
    companyUuid,
    searchTerm,
    page,
  });

const clientsGetDeletable = () =>
  apiFetch<Client[]>("GET", "/clients/get-deletable");

const clientsGetByUuid = (uuid: string) =>
  apiFetch<Client>("GET", `/clients/get/${uuid}`);

const clientsGetSellers = () =>
  apiFetch<{ label: string; value: string }[]>("GET", "/clients/get-sellers/");

const clientsUpdate = (uuid: string, client: Client) =>
  apiFetch<Ok>("POST", `/clients/update/${uuid}`, null, client);

const clientsCreate = (client: Client) =>
  apiFetch<Ok>("POST", "/clients/create", null, client);

const clientsDelete = (uuid: string) =>
  apiFetch<Ok>("POST", `/clients/delete/${uuid}`);

const myQuotesSearchClients = (searchTerm: string, page: string) =>
  apiFetch<ListResponse<Client>>("GET", "/my-quotes/search-clients", {
    searchTerm,
    page,
  });

const myQuotesCreate = (clientUuid: string, quote: Quote) =>
  apiFetch<Ok>("POST", "/my-quotes/create", null, { clientUuid, quote });

const myQuotesUpdate = (uuid: string, quote: Quote) =>
  apiFetch<Ok>("POST", `/my-quotes/update/${uuid}`, null, quote);

export type MyQuotesListResponse = {
  uuid: string;
  quote: Quote;
  clientName: string;
};
const myQuotesList = (ownerType: string, searchTerm: string, page: string) =>
  apiFetch<ListResponse<MyQuotesListResponse>>(
    "GET",
    `/my-quotes/list/${ownerType}`,
    {
      searchTerm,
      page,
    }
  );

const myQuotesGetClient = (clientUuid: string) =>
  apiFetch<Client>("GET", `/my-quotes/get-client/${clientUuid}`);

const myQuotesGetCategories = () =>
  apiFetch<Category[]>("GET", "/my-quotes/get-categories");

const myQuotesGetEquipments = () =>
  apiFetch<Equipment[]>("GET", "/my-quotes/get-equipments");

export type MyQuotesGetByUuidResponse = {
  quote: Quote;
  client: Client;
  seller: User;
  equipmentMap: Record<string, string>;
};
const myQuotesGetByUuid = (uuid: string) =>
  apiFetch<MyQuotesGetByUuidResponse>("GET", `/my-quotes/get/${uuid}`);

export type MyQuotesQueueResponse = {
  allocated: MyQuotesListResponse[];
  queued: MyQuotesListResponse[];
  pending: MyQuotesListResponse[];
  contractPending: MyQuotesListResponse[];
  remaining: MyQuotesListResponse[];
};
const myQuotesQueue = () =>
  apiFetch<MyQuotesQueueResponse>("GET", "/my-quotes/queue");

export type QuotesAdminListResponse = {
  quote: Quote;
  client: Client;
  seller: User;
};
const quotesAdminList = (state: string, searchTerm: string, page: string) =>
  apiFetch<ListResponse<QuotesAdminListResponse>>(
    "GET",
    `/quotes-admin/list/${state}`,
    {
      searchTerm,
      page,
    }
  );

const quotesAdminApprove = (uuid: string) =>
  apiFetch<Ok>("POST", `/quotes-admin/approve/${uuid}`);

const quotesAdminAuthorize = (uuid: string) =>
  apiFetch<Ok>("POST", `/quotes-admin/authorize/${uuid}`);

const quotesAdminRemove = (uuid: string) =>
  apiFetch<Ok>("POST", `/quotes-admin/remove/${uuid}`);

const quotesAdminEmail = (uuid: string, email: string) =>
  apiFetch<Ok>("POST", `/quotes-admin/email/${uuid}`, null, { email });

export type AuthorizationsListResponse = {
  uuid: string;
  quote: Quote;
  clientName: string;
  sellerName: string;
};
const authorizationsList = (
  taskFilter: string,
  searchTerm: string,
  page: string
) =>
  apiFetch<ListResponse<AuthorizationsListResponse> & { pendingCount: number }>(
    "GET",
    "/authorizations/list",
    {
      searchTerm,
      page,
      taskFilter,
    }
  );

const authorizationsSetTasks = (uuid: string, tasks: QuoteTasks) =>
  apiFetch<Ok>("POST", `/authorizations/set-tasks/${uuid}`, null, tasks);

const authorizationsAddNote = (uuid: string, text: string) =>
  apiFetch<QuoteNote[]>("POST", `/authorizations/add-note/${uuid}`, null, {
    text,
  });

const authorizationsDeleteNote = (uuid: string, index: number) =>
  apiFetch<QuoteNote[]>("POST", `/authorizations/delete-note/${uuid}`, null, {
    index,
  });

const authorizationsSetIsCancelled = (uuid: string, value: boolean) =>
  apiFetch<Ok>("POST", `/authorizations/set-cancelled/${uuid}`, null, {
    value,
  });

export type QuoteWithClient = {
  quote: Quote;
  client: Client;
};
const contractsGet = () => apiFetch<QuoteWithClient[]>("GET", "/contracts/get");

const contractsUpdate = (
  uuid: string,
  contract: "rent" | "monitoring",
  contractInfo: ContractInfo
) =>
  apiFetch<Ok>("POST", "/contracts/update", null, {
    uuid,
    contract,
    contractInfo,
  });

const contractsComplete = (uuid: string) =>
  apiFetch<Ok>("POST", "/contracts/complete", null, { uuid });

const contractsRender = (uuid: string) =>
  apiFetch<{
    quote: Quote;
    client: Client;
    company: Company;
    equipmentMap: Record<string, string>;
  }>("GET", `/contracts/render/${uuid}`);

export type InstallationsGetResponse = {
  state: DashboardState;
  installationsMap: InstallationsMap;
  techniciansMap: TechniciansMap;
};
const installationsGet = () =>
  apiFetch<InstallationsGetResponse>("GET", "/installations/get");

const installationsUpdate = (data: DashboardState) =>
  apiFetch<Ok>("POST", "/installations/update", null, data);

const installationsMarkComplete = (uuid: string) =>
  apiFetch<Ok>("POST", `/installations/mark-complete/${uuid}`);

const installationsAddNote = (uuid: string, text: string) =>
  apiFetch<QuoteNote[]>("POST", `/installations/add-note/${uuid}`, null, {
    text,
  });
const externalGetQuote = (hash: string) =>
  apiFetch<{
    quote: Quote;
    client: Client;
    seller: User;
    equipmentMap: Record<string, string>;
  }>("GET", `/external/get-quote/${hash}`);

const externalGetQuoteForAuthorization = (hash: string) =>
  apiFetch<{
    quote: Quote;
    client: Client;
  }>("GET", `/external/get-quote-for-authorization/${hash}`);

const externalAuthorizeQuote = (hash: string) =>
  apiFetch<Ok>("POST", `/external/authorize-quote`, null, { hash });

const externalGetRentReceipt = (hash: string) =>
  apiFetch<{
    receipt: RentReceipt;
    contract: RentContract;
    company: Company;
  }>("GET", `/external/get-rent-receipt/${hash}`);

const externalGetGenericReceipt = (hash: string) =>
  apiFetch<{
    receipt: Receipt;
    clientName: string;
  }>("GET", `/external/get-generic-receipt/${hash}`);

const equipmentsList = (
  blocked: string,
  category: string,
  searchTerm: string,
  page: string
) =>
  apiFetch<ListResponse<Equipment>>("GET", "/equipments/list", {
    blocked,
    category,
    searchTerm,
    page,
  });

const equipmentsListCategories = () =>
  apiFetch<Category[]>("GET", "/equipments/list-categories");

const equipmentsCreateCategory = (name: string) =>
  apiFetch<Ok>("POST", "/equipments/create-category", null, { name });

const equipmentsGetByUuid = (uuid: string) =>
  apiFetch<Equipment>("GET", `/equipments/get/${uuid}`);

const equipmentsUpdate = (uuid: string, equipment: Equipment) =>
  apiFetch<Ok>("POST", `/equipments/update/${uuid}`, null, equipment);

const equipmentsCreate = (equipment: Equipment) =>
  apiFetch<Ok>("POST", "/equipments/create", null, equipment);

export type FinanceContractClientsResponse = {
  uuid: string;
  client: Client;
  activeContracts: number;
  totalContracts: number;
};
const financeContractClients = (
  activeClients: string,
  searchTerm: string,
  page: string
) =>
  apiFetch<ListResponse<FinanceContractClientsResponse>>(
    "GET",
    "/finance/contracts/clients",
    {
      searchTerm,
      page,
      activeClients,
    }
  );

export type FinanceContractClientResponse = {
  client: Client;
  contracts: {
    contract: RentContract;
    totalReceipts: number;
  }[];
};
const financeContractClient = (uuid: string) =>
  apiFetch<FinanceContractClientResponse>(
    "GET",
    `/finance/contracts/client/${uuid}`
  );

const financeCreateContract = (contract: RentContract) =>
  apiFetch<Ok & { uuid?: string }>(
    "POST",
    "/finance/contracts/create",
    null,
    contract
  );

export type FinanceGetContractResponse = {
  contract: RentContract;
  plan: RentContractPlan;
};
const financeGetContract = (uuid: string) =>
  apiFetch<FinanceGetContractResponse>("GET", `/finance/contracts/${uuid}`);

export type FinanceListPendingResponse = {
  contract: RentContract;
  clientName: string;
  missingRefs: string[];
}[];
const financeListPending = () =>
  apiFetch<FinanceListPendingResponse>(
    "GET",
    "/finance/rent-receipts/list-pending"
  );

const financeIssueRent = (contractUuid: string, installment: string) =>
  apiFetch<Ok & { uuid?: string; externalHash?: string }>(
    "POST",
    "/finance/rent-receipts/issue",
    null,
    {
      contractUuid,
      installment,
    }
  );

const financeCancelRent = (uuid: string) =>
  apiFetch<Ok>("POST", `/finance/rent-receipts/cancel/${uuid}`);

const financeEmailRent = (uuid: string) =>
  apiFetch<Ok>("POST", `/finance/rent-receipts/email/${uuid}`);

const financeUpdateField = (
  contractUuid: string,
  field: string,
  value: UIValue | string[] | RentContractClient
) =>
  apiFetch<Ok>("POST", `/finance/contract/update-field/${contractUuid}`, null, {
    field,
    value,
  });

const financeReceiptsClients = (searchTerm: string, page: string) =>
  apiFetch<ListResponse<Client>>("GET", "/finance/receipts/clients", {
    searchTerm,
    page,
  });

export type FinanceReceiptsClientResponse = {
  client: Client;
  receipts: {
    uuid: string;
    receipt: Receipt;
    userName: string;
  }[];
};
const financeReceiptsClient = (uuid: string) =>
  apiFetch<FinanceReceiptsClientResponse>(
    "GET",
    `/finance/receipts/client/${uuid}`
  );

const financeCreateReceipt = (clientUuid: string, receipt: Receipt) =>
  apiFetch<Ok>("POST", `/finance/receipts/create/${clientUuid}`, null, receipt);

const financeCancelGeneric = (uuid: string) =>
  apiFetch<Ok>("POST", `/finance/receipts/cancel/${uuid}`);

const financeEmailGeneric = (uuid: string, email: string) =>
  apiFetch<Ok>("POST", `/finance/receipts/email/${uuid}`, null, { email });

export type FinanceReportResponse = {
  items: {
    receipt: RentReceipt;
    client: Client;
  }[];
  company: Company;
};
const financeReport = (companyUuid: string, ref: string) =>
  apiFetch<FinanceReportResponse>(
    "GET",
    `/finance/report/${companyUuid}/${ref}`
  );

const statsGet = (period: string, groupBy: "events" | "creation") =>
  apiFetch<StatsData>("GET", `/stats/get/${period}/${groupBy}`);

export type TechnicalAuthorizationsListItem = {
  uuid: string;
  quote: Quote;
  clientName: string;
};
const technicalAuthorizationsList = (searchTerm: string, page: string) =>
  apiFetch<ListResponse<TechnicalAuthorizationsListItem>>(
    "GET",
    "/technical/list-authorizations",
    { searchTerm, page }
  );

const technicalClientsList = (
  searchTerm: string,
  page: string
): Promise<ListResponse<Client>> =>
  apiFetch("GET", "/technical/list-clients", { searchTerm, page });

const technicalGetOrder = (uuid: string) =>
  apiFetch<{
    quote: Quote;
    client: Client;
    equipmentMap: Record<string, string>;
  }>("GET", `/technical/get-order/${uuid}`);

const technicalGetClient = (uuid: string) =>
  apiFetch<Client>("GET", `/technical/get-client/${uuid}`);

const technicalAddNote = (uuid: string, text: string) =>
  apiFetch<ClientTechnicalNote[]>("POST", `/technical/add-note/${uuid}`, null, {
    text,
  });

const technicalDeleteNote = (uuid: string, index: number) =>
  apiFetch<ClientTechnicalNote[]>(
    "POST",
    `/technical/delete-note/${uuid}`,
    null,
    { index }
  );

const pushSubscribe = (userUuid: string, subscription: PushSubscription) =>
  apiFetch<Ok>("POST", `/push/subscribe`, null, {
    userUuid,
    subscription,
  });

const pushCheck = (endpoint: string) =>
  apiFetch<{ sync: boolean }>("POST", `/push/check`, null, { endpoint });

const pushTest = () => apiFetch<Ok>("POST", `/push/test`);

const pushUnsubscribe = (endpoint: string) =>
  apiFetch<Ok>("POST", `/push/unsubscribe`, null, { endpoint });

const pushSave = (notifications: Notifications) =>
  apiFetch<Ok>("POST", `/push/save`, null, notifications);

const authorizationsTogglePush = (uuid: string) =>
  apiFetch<Ok>("POST", `/authorizations/toggle-push`, null, { uuid });

const Api = {
  version,
  login,
  me,
  getCompanies,
  usersGetAll,
  usersGet,
  usersUpdate,
  usersCreate,
  clientsSearch,
  clientsGetDeletable,
  clientsGetByUuid,
  clientsGetSellers,
  clientsUpdate,
  clientsCreate,
  clientsDelete,
  myQuotesSearchClients,
  myQuotesCreate,
  myQuotesUpdate,
  myQuotesList,
  myQuotesGetClient,
  myQuotesGetCategories,
  myQuotesGetEquipments,
  myQuotesGetByUuid,
  myQuotesQueue,
  quotesAdminList,
  quotesAdminApprove,
  quotesAdminAuthorize,
  quotesAdminRemove,
  quotesAdminEmail,
  authorizationsList,
  authorizationsSetTasks,
  authorizationsAddNote,
  authorizationsDeleteNote,
  authorizationsSetIsCancelled,
  contractsGet,
  contractsUpdate,
  contractsComplete,
  contractsRender,
  installationsGet,
  installationsUpdate,
  installationsMarkComplete,
  installationsAddNote,
  externalGetQuote,
  externalGetQuoteForAuthorization,
  externalAuthorizeQuote,
  externalGetRentReceipt,
  externalGetGenericReceipt,
  equipmentsList,
  equipmentsListCategories,
  equipmentsCreateCategory,
  equipmentsGetByUuid,
  equipmentsUpdate,
  equipmentsCreate,
  financeContractClients,
  financeContractClient,
  financeCreateContract,
  financeGetContract,
  financeListPending,
  financeIssueRent,
  financeCancelRent,
  financeEmailRent,
  financeUpdateField,
  financeReceiptsClients,
  financeReceiptsClient,
  financeCreateReceipt,
  financeCancelGeneric,
  financeEmailGeneric,
  financeReport,
  statsGet,
  technicalAuthorizationsList,
  technicalClientsList,
  technicalGetOrder,
  technicalGetClient,
  technicalAddNote,
  technicalDeleteNote,
  pushSubscribe,
  pushCheck,
  pushTest,
  pushUnsubscribe,
  pushSave,
  authorizationsTogglePush,
};
export default Api;
